// (Not named *Switch: the module-switch drift guard owns that name for the in-module page navs.)
// The two ways into EOS-07's busbar work (EOS-07a and EOS-07c): selecting a busbar to
// KES-EL-BUS-002, and checking a builder's declared busbar. The mode is part of the page's
// address (?mode=), so Back, Forward and a shared link bring the same mode back.

export const BUSBAR_MODES = ["selection", "evidence"] as const;

export type BusbarMode = (typeof BUSBAR_MODES)[number];

export const DEFAULT_BUSBAR_MODE: BusbarMode = "selection";

/** The mode a query value names; a missing or unknown value is the default, selection. */
export function busbarModeOf(value: string | null): BusbarMode {
  return (BUSBAR_MODES as readonly string[]).includes(value ?? "")
    ? (value as BusbarMode)
    : DEFAULT_BUSBAR_MODE;
}

// Exhaustive map: a new mode without words is a compile error.
export const BUSBAR_MODE_WORDS: Record<BusbarMode, { name: string; description: string }> = {
  selection: {
    name: "Select a busbar",
    description: "Choose Cu/Al busbar size and arrangement to KES-EL-BUS-002 Rev 0",
  },
  evidence: {
    name: "Check with the builder's evidence",
    description: "Check a panel builder's declared busbar against the study",
  },
};

type BusbarModePickerProps = {
  mode: BusbarMode;
  onChange: (mode: BusbarMode) => void;
};

// A native radio group: it is one tab stop, the arrow keys move the choice, and the name and
// the one-line description of each mode are read out. Changing the mode never touches a draft:
// each form keeps its own under its own module key.
export function BusbarModePicker({ mode, onChange }: BusbarModePickerProps) {
  return (
    <fieldset data-busbar-mode-picker>
      <legend>Mode</legend>
      {BUSBAR_MODES.map((option) => (
        <div key={option} data-busbar-mode={option}>
          <label>
            <input
              type="radio"
              name="busbar-mode"
              value={option}
              checked={mode === option}
              aria-describedby={`busbar-mode-${option}-description`}
              onChange={() => onChange(option)}
            />
            {BUSBAR_MODE_WORDS[option].name}
          </label>
          <p id={`busbar-mode-${option}-description`}>{BUSBAR_MODE_WORDS[option].description}</p>
        </div>
      ))}
    </fieldset>
  );
}

export default BusbarModePicker;
